import { WebsiteContentService } from "../../services/WebsiteContentService";
import { processImageForUpload } from "../../utils/imageUtils";
import { useApi } from "./useApi";
import { useConfiguration } from "./useConfiguration";

// Keep individual requests reasonably sized; after client-side processing each
// file is a ~200–600 KB JPEG so this allows many files per batch.
const MAX_CHUNK_BYTES = 10 * 1024 * 1024;

const uploadFilesInChunks = async (files, uploadChunk) => {
    if (files.length === 0) return null;

    const chunks = [];
    let current = [], currentSize = 0;
    for (const file of files) {
        if (currentSize + file.size > MAX_CHUNK_BYTES && current.length > 0) {
            chunks.push(current);
            current = [file];
            currentSize = file.size;
        } else {
            current.push(file);
            currentSize += file.size;
        }
    }
    if (current.length > 0) chunks.push(current);

    let last = null;
    for (const chunk of chunks) last = await uploadChunk(chunk);
    return last;
};

export const useWebsiteContentApi = () => {
    const { executeApiOperation } = useApi();
    const { appConfig } = useConfiguration();

    const websiteContentService = new WebsiteContentService(appConfig.apiBaseUrl);

    const findAll = async (propertyId) =>
        executeApiOperation(() => websiteContentService.findAll(propertyId));

    const findById = async (propertyId, componentId) =>
        executeApiOperation(() => websiteContentService.findById(propertyId, componentId));

    const createComponent = async (propertyId, component) =>
        executeApiOperation(() => websiteContentService.create(propertyId, component));

    const updateComponent = async (propertyId, componentId, component) =>
        executeApiOperation(() => websiteContentService.update(propertyId, componentId, component));

    const deleteComponent = async (propertyId, componentId) =>
        executeApiOperation(() => websiteContentService.delete(propertyId, componentId));

    const uploadImages = async (propertyId, componentId, files) => {
        const processed = await Promise.all(files.map(processImageForUpload));
        return uploadFilesInChunks(processed, (chunk) =>
            executeApiOperation(() => websiteContentService.uploadImages(propertyId, componentId, chunk))
        );
    };

    const deleteImages = async (propertyId, componentId, fileNames) =>
        executeApiOperation(() => websiteContentService.deleteImages(propertyId, componentId, fileNames));

    return {
        findAll,
        findById,
        createComponent,
        updateComponent,
        deleteComponent,
        uploadImages,
        deleteImages,
    };
};
